import { ChangeEvent, useEffect, useRef, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import Loading from "../../shared/components/Loading";
import Empty from "../../shared/components/Empty";
import ErrorMessage from "../../shared/components/Error";
import AgentMonitorService, {
  AgentConversation,
  AgentMonitorAgent
} from "../../../services/AgentMonitorService";
import "./agentMonitor.css";

interface ReassignDrawerProps {
  agent: AgentMonitorAgent;
  agentDestinations: AgentMonitorAgent[];
  onClose: () => void;
  onSuccess: () => void;
}

const UNASSIGN_VALUE = "unassign";

const ReassignDrawer = ({
  agent,
  agentDestinations,
  onClose,
  onSuccess
}: ReassignDrawerProps) => {
  const { translate } = TranslationService;

  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState(false);
  const [conversations, setConversations] = useState<AgentConversation[]>([]);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [targetAgentValue, setTargetAgentValue] = useState<string>("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const allSelected: boolean =
    conversations.length > 0 && selectedIds.length === conversations.length;
  const someSelected: boolean = selectedIds.length > 0 && !allSelected;

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.agentId]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const fetchConversations = async () => {
    setConversationsLoading(true);
    setConversationsError(false);
    setSelectedIds([]);
    const result = await AgentMonitorService.getOpenChats(agent.agentId);
    setConversationsLoading(false);
    if (result instanceof Error) {
      setConversationsError(true);
      return;
    }
    setConversations(result);
  };

  const toggleConversation = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllConversations = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedIds(
      event.target.checked ? conversations.map((x) => x.conversationId) : []
    );
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    setConfirming(true);
    setConfirmError(null);
    const toAgentId =
      targetAgentValue === UNASSIGN_VALUE ? null : Number(targetAgentValue);
    const result = await AgentMonitorService.reassign({
      FromAgentId: agent.agentId,
      ToAgentId: toAgentId,
      MessageIds: selectedIds
    });
    setConfirming(false);
    if (result instanceof Error) {
      setConfirmError(result.message || translate.ErrorProcessingRequest);
      return;
    }
    onSuccess();
  };

  const isUnassign = targetAgentValue === UNASSIGN_VALUE;
  const canConfirm =
    selectedIds.length > 0 && targetAgentValue !== "" && !confirming;

  // Exclude the source agent and the "Sin asignar" bucket from destination list
  const destinations = agentDestinations.filter(
    (a) => a.agentId !== agent.agentId && a.agentId !== null
  );

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />
      <div
        className="offcanvas offcanvas-end show"
        style={{
          zIndex: 1045,
          width: 480,
          visibility: "visible",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <div
          className="offcanvas-header border-bottom"
          style={{ padding: "16px 20px" }}
        >
          <h6 className="offcanvas-title mb-0" style={{ fontWeight: 600 }}>
            {translate.ReassignConversations}
          </h6>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div
          className="offcanvas-body d-flex flex-column"
          style={{ padding: "16px 20px", flex: 1, overflowY: "auto" }}
        >
          <div className="am-from-tag mb-3">
            {translate.From}: <strong>{agent.agentName}</strong> (
            {agent.openChats} {translate.OpenChats})
          </div>

          <div className="am-info-box mb-3">
            {translate.SelectConversationsToReassign}
          </div>

          {conversationsLoading && <Loading height={150} />}
          {!conversationsLoading && conversationsError && (
            <ErrorMessage
              message={translate.ErrorLoadingList}
              onRefresh={fetchConversations}
            />
          )}
          {!conversationsLoading && !conversationsError && (
            <>
              <div
                className="am-conv-table-wrap mb-3"
                style={{ overflowY: "auto", maxHeight: 280 }}
              >
                {conversations.length === 0 ? (
                  <Empty />
                ) : (
                  <table className="am-conv-table">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}>
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            className="form-check-input flex-shrink-0 mt-0"
                            checked={allSelected}
                            disabled={conversations.length === 0}
                            onChange={toggleAllConversations}
                            title={translate.CheckAll}
                            aria-label={translate.CheckAll}
                            style={{ cursor: "pointer" }}
                          />
                        </th>
                        <th>{translate.OpenConversations}</th>
                        <th className="am-text-end">{translate.LastMessage}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations.map((conv) => (
                        <tr
                          key={conv.conversationId}
                          className={
                            selectedIds.includes(conv.conversationId)
                              ? "am-selected"
                              : ""
                          }
                          onClick={() =>
                            toggleConversation(conv.conversationId)
                          }
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input flex-shrink-0"
                              checked={selectedIds.includes(
                                conv.conversationId
                              )}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() =>
                                toggleConversation(conv.conversationId)
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </td>
                          <td>
                            <div className="am-conv-client">
                              {conv.clientName}
                            </div>
                            <div className="am-conv-preview">
                              {conv.lastMessage}
                            </div>
                          </td>
                          <td className="am-text-end">
                            {conv.lastMessageTime}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="mb-2">
                <label
                  className="form-label"
                  style={{ fontSize: "0.875rem", fontWeight: 500 }}
                >
                  {translate.AssignTo}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={targetAgentValue}
                  onChange={(e) => setTargetAgentValue(e.target.value)}
                >
                  <option value="" disabled>
                    {translate.SelectAnAgent}
                  </option>
                  {destinations.map((dest) => (
                    <option key={dest.agentId} value={String(dest.agentId)}>
                      {dest.agentName} (
                      {dest.available
                        ? translate.AgentAvailable
                        : translate.AgentNotAvailable}
                      )
                    </option>
                  ))}
                  <option value={UNASSIGN_VALUE}>— {translate.Unassign}</option>
                </select>
                {isUnassign && (
                  <p
                    className="text-muted mt-1 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {translate.UnassignConversationsHelp}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-top d-flex flex-column gap-2 p-3">
          {confirmError && (
            <div
              className="alert alert-danger py-2 mb-1"
              style={{ fontSize: "0.85rem" }}
            >
              {confirmError}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
              disabled={confirming}
            >
              {translate.Cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {confirming ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {translate.Confirm}
                </>
              ) : (
                translate.Confirm
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReassignDrawer;
